/**
 * Utilitaires pour le calcul du score de suspicion de triche
 * Partagé entre le frontend (ent-struct) et l'admin
 */

// ============================================
// TYPES
// ============================================

/**
 * Type minimal requis pour les calculs de triche — compatible avec
 * EntStructMessage (admin-portail) et les messages Convex.
 */
export interface MessageCheating {
  role: string;
  cheatingMeta?: CheatingMeta;
}

export interface CheatingStats {
  scoreMoyen: number;
  scoreMax: number;
  totalChangementsOnglet: number;
  totalTempsHorsPage: number;
  pasteDetecte: boolean;
  nombrePastes: number;
  messagesSuspects: number;
  messagesAvecMeta: number;
  /** Vitesse de frappe la plus élevée observée (car/s) */
  vitesseFrappeMax: number;
  /** Nombre de messages dont la vitesse de frappe est jugée anormale */
  messagesVitesseSuspecte: number;
}

export interface CheatingMeta {
  // Données temporelles (en millisecondes)
  tempsAffichageQuestion: number;
  tempsRedaction: number;
  tempsTotal: number;

  // Données de frappe
  vitesseFrappe: number;
  nombreEdits: number;
  pasteDetecte: boolean;

  // Données navigateur
  changementsOnglet: number;
  tempsHorsPage: number;
  absences: Array<{ debut: number; fin: number; duree: number }>;

  /** Longueur du message envoyé, nécessaire pour qualifier la vitesse de frappe */
  longueurMessage?: number;

  // Score calculé (0-100)
  scoreSuspicion: number;
}

export interface CheatingMetaInput {
  tempsAffichageQuestion: number;
  tempsRedaction: number;
  tempsTotal: number;
  vitesseFrappe: number;
  nombreEdits: number;
  pasteDetecte: boolean;
  changementsOnglet: number;
  tempsHorsPage: number;
  absences: Array<{ debut: number; fin: number; duree: number }>;
  longueurMessage: number;
}

// ============================================
// FONCTIONS DE CALCUL
// ============================================

/**
 * Seuil de vitesse de frappe (caractères par seconde) au-delà duquel une
 * réponse est considérée comme anormalement rapide.
 */
export const SEUIL_VITESSE_FRAPPE = 10;

/**
 * Longueur minimale du message pour que la vitesse de frappe soit
 * significative : une réponse courte ("oui") tapée vite n'est pas suspecte.
 */
export const LONGUEUR_MIN_VITESSE_FRAPPE = 100;

/**
 * Vrai si la vitesse de frappe est anormale sur un message assez long pour
 * que la mesure ait du sens.
 */
export function estVitesseSuspecte(
  vitesseFrappe: number,
  longueurMessage: number | undefined,
): boolean {
  return (
    vitesseFrappe > SEUIL_VITESSE_FRAPPE &&
    (longueurMessage ?? 0) >= LONGUEUR_MIN_VITESSE_FRAPPE
  );
}

/**
 * Calcule le score de suspicion basé sur les métadonnées collectées
 * - 0 : Faible (aucun indicateur)
 * - 1 : Moyen (copier-coller, changement d'onglet et/ou vitesse de frappe
 *   anormale)
 */
export function calculerScoreSuspicion(meta: CheatingMetaInput): number {
  const suspect =
    meta.pasteDetecte ||
    meta.changementsOnglet >= 1 ||
    estVitesseSuspecte(meta.vitesseFrappe, meta.longueurMessage);
  return suspect ? 1 : 0;
}

/**
 * Interprète le score de suspicion et retourne un niveau et une couleur
 */
export function interpreterScore(score: number): {
  niveau: "faible" | "moyen";
  couleur: string;
  description: string;
} {
  if (score === 0) {
    return {
      niveau: "faible",
      couleur: "text-green-500",
      description: "",
    };
  }
  return {
    niveau: "moyen",
    couleur: "text-orange-500",
    description: "",
  };
}

/**
 * Agrège les stats de triche à partir d'une liste de messages.
 * Compatible avec EntStructMessage[] (admin) et les messages Convex.
 */
export function calculerStatsCheating(messages: MessageCheating[]): CheatingStats {
  const messagesUser = messages.filter(
    (m) => m.role === "user" && m.cheatingMeta,
  );

  if (messagesUser.length === 0) {
    return {
      scoreMoyen: 0,
      scoreMax: 0,
      totalChangementsOnglet: 0,
      totalTempsHorsPage: 0,
      pasteDetecte: false,
      nombrePastes: 0,
      messagesSuspects: 0,
      messagesAvecMeta: 0,
      vitesseFrappeMax: 0,
      messagesVitesseSuspecte: 0,
    };
  }

  const scores = messagesUser.map((m) => m.cheatingMeta!.scoreSuspicion);
  const scoreMoyen = Math.round(
    scores.reduce((a, b) => a + b, 0) / scores.length,
  );
  const scoreMax = Math.max(...scores);
  const totalChangementsOnglet = messagesUser.reduce(
    (sum, m) => sum + m.cheatingMeta!.changementsOnglet,
    0,
  );
  const totalTempsHorsPage = messagesUser.reduce(
    (sum, m) => sum + m.cheatingMeta!.tempsHorsPage,
    0,
  );
  const nombrePastes = messagesUser.filter(
    (m) => m.cheatingMeta!.pasteDetecte,
  ).length;
  const pasteDetecte = nombrePastes > 0;
  const messagesSuspects = messagesUser.filter(
    (m) => m.cheatingMeta!.scoreSuspicion > 0,
  ).length;
  const vitesseFrappeMax = messagesUser.reduce(
    (max, m) => Math.max(max, m.cheatingMeta!.vitesseFrappe),
    0,
  );
  const messagesVitesseSuspecte = messagesUser.filter((m) =>
    estVitesseSuspecte(
      m.cheatingMeta!.vitesseFrappe,
      m.cheatingMeta!.longueurMessage,
    ),
  ).length;

  return {
    scoreMoyen,
    scoreMax,
    totalChangementsOnglet,
    totalTempsHorsPage,
    pasteDetecte,
    nombrePastes,
    messagesSuspects,
    messagesAvecMeta: messagesUser.length,
    vitesseFrappeMax,
    messagesVitesseSuspecte,
  };
}

/**
 * Retourne true si au moins un indicateur de triche est détecté :
 * copier-coller, changements d'onglet ou vitesse de frappe anormale sur
 * l'ensemble des messages.
 * Les conditions sont alignées sur les items affichés dans le tooltip
 * "Raisons de la détection" de ScoreGlobalHeader.
 */
export function estSuspect(messages: MessageCheating[]): boolean {
  const stats = calculerStatsCheating(messages);
  return (
    stats.messagesAvecMeta > 0 &&
    (stats.pasteDetecte ||
      stats.totalChangementsOnglet > 0 ||
      stats.messagesVitesseSuspecte > 0)
  );
}

/**
 * Calcule la vitesse de frappe moyenne (caractères par seconde)
 */
export function calculerVitesseFrappe(
  nombreCaracteres: number,
  tempsRedactionMs: number,
): number {
  if (tempsRedactionMs <= 0 || nombreCaracteres <= 0) {
    return 0;
  }
  return Number((nombreCaracteres / (tempsRedactionMs / 1000)).toFixed(2));
}
